import React, {
  memo, forwardRef,
} from 'react';
import {
  Col, Form, Input, Modal, Row,
} from 'antd';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { PASSWORD_REGEX } from '@constants/UHESettings';
import { ExclamationCircleTwoTone } from '@ant-design/icons';

const ChangePasswordModal = memo(forwardRef(({
  onSubmit,
  user,
  onCancel,
}, ref) => {
  const intl = useIntl();

  return (
    <Modal
      visible={user?.id}
      onOk={onSubmit}
      onCancel={onCancel}
      okText={intl.formatMessage({ id: 'configuration.users.updatePassword' })}
      afterClose={ref.current?.resetFields}
    >
      <Row gutter={16}>
        <Col>
          <ExclamationCircleTwoTone className="modal__icon modal__icon--warning" twoToneColor="#fa8c16" />
        </Col>
        <Col flex="auto">
          <div className="modal__title">
            {`${intl.formatMessage({ id: 'configuration.users.changePassword' })} ${user?.firstName} ${user?.lastName}`}
          </div>
          <Form
            autoComplete={Math.random()}
            layout="vertical"
            ref={ref}
            className="changePassword"
            onFinish={onSubmit}
          >
            <Form.Item
              label={intl.formatMessage({ id: 'configuration.users.newPassword' })}
              name="password"
              hasFeedback
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({ id: 'configuration.users.emptyField' }),
                },
                {
                  pattern: PASSWORD_REGEX,
                  message: intl.formatMessage({ id: 'configuration.users.newPassRule' }),
                },
              ]}
            >
              <Input
                autoComplete={Math.random()}
                id="password"
                type="password"
                autoFocus
              />
            </Form.Item>
            <Form.Item
              label={intl.formatMessage({ id: 'configuration.users.confirmPassword' })}
              name="confirmPassword"
              dependencies={['password']}
              hasFeedback
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({ id: 'configuration.users.emptyField' }),
                },
                ({ getFieldValue }) => ({
                  validator(rule, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(intl.formatMessage({ id: 'configuration.users.confirmField' }));
                  },
                }),
              ]}
            >
              <Input
                autoComplete={Math.random()}
                id="confirmPassword"
                type="password"
              />
            </Form.Item>
            {/* For submitting on enter keypress */}
            <input autoComplete={Math.random()} type="submit" hidden />
          </Form>
        </Col>
      </Row>
    </Modal>
  );
}));

ChangePasswordModal.displayName = 'ChangePasswordModal';

ChangePasswordModal.defaultProps = {
  user: null,
};

ChangePasswordModal.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  user: PropTypes.shape({
    id: PropTypes.number,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
  }),
  onCancel: PropTypes.func.isRequired,
};

export default ChangePasswordModal;
